import { fail, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readDocumentProperties } from "@/office/powerpoint/design";
import { nichtPruefbar } from "../schnittstelle";

export const lektionVorDemVersand: Lesson = {
  id: "vor-dem-versand",
  title: "Vor dem Versand",
  summary: "Was in der Datei steckt, was niemand sehen soll – und der Titel, den alle sehen.",
  durationMinutes: 9,
  learningGoals: [
    "Die Dokumentprüfung einsetzen",
    "Dokumenteigenschaften bewusst pflegen",
    "Verborgene Inhalte vor der Weitergabe entfernen",
  ],
  steps: [
    {
      id: "dokumentpruefung",
      title: "Die Dokumentprüfung",
      explanation: [
        "Eine PowerPoint-Datei enthält mehr, als auf den Folien zu sehen ist: den Namen " +
          "des Autors und aller Bearbeitenden, Kommentare, ausgeblendete Folien, " +
          "Sprechernotizen, eingebettete Arbeitsmappen mit allen Tabellenblättern und die " +
          "abgeschnittenen Teile zugeschnittener Bilder.",
        "Vor der Weitergabe an Externe lohnt sich deshalb „Datei“ → „Informationen“ → " +
          "„Auf Probleme überprüfen“ → „Dokument prüfen“. Die Prüfung listet auf, was " +
          "gefunden wurde, und bietet zu jedem Punkt an, ihn zu entfernen.",
        "Zwei Warnungen dazu. Erstens ist das Entfernen endgültig und lässt sich nicht " +
          "rückgängig machen – deshalb vorher speichern oder auf einer Kopie arbeiten. " +
          "Zweitens entfernt „Alle entfernen“ bei den Sprechernotizen genau das, was man " +
          "für die Nachlese vielleicht behalten wollte. Die Liste will gelesen werden, " +
          "nicht blind abgearbeitet.",
        "An derselben Stelle stehen zwei weitere Prüfungen: „Barrierefreiheit " +
          "überprüfen“ aus dem gleichnamigen Modul und „Kompatibilität überprüfen“, das " +
          "meldet, welche Elemente in älteren PowerPoint-Versionen anders aussehen.",
      ],
      keyPoints: [
        "Datei → Informationen → Auf Probleme überprüfen → Dokument prüfen.",
        "Das Entfernen ist endgültig – vorher speichern oder eine Kopie nutzen.",
        "Die Liste lesen, statt „Alle entfernen“ zu drücken.",
      ],
    },
    {
      id: "dokumenteigenschaften",
      title: "Dokumenteigenschaften",
      explanation: [
        "Unter „Datei“ → „Informationen“ steht rechts eine Spalte mit den " +
          "Dokumenteigenschaften: Titel, Autor, Firma, Stichwörter. Die meisten davon " +
          "bleiben leer oder tragen, was PowerPoint aus der Installation ableiten " +
          "konnte.",
        "Der Titel lohnt die halbe Minute, die er kostet. Er erscheint als Fenstertitel, " +
          "in Suchergebnissen, in Dokumentbibliotheken, beim Teilen über Teams und " +
          "SharePoint – und in einer exportierten PDF als Dokumenttitel, den " +
          "Screenreader vorlesen. Steht dort nichts, zeigen alle diese Stellen den " +
          "Dateinamen.",
        "PowerPoint füllt den Titel automatisch mit dem Text der ersten Titelfolie, " +
          "sobald man die Präsentation speichert – aber nur, wenn dieser Text in einem " +
          "Titelplatzhalter steht. Auch hier zahlt sich der rote Faden des Kurses direkt " +
          "aus.",
        "Der Autorenname ist der andere Punkt, den man kennen sollte: Er wird aus der " +
          "Office-Installation übernommen und geht bei jeder Weitergabe mit. Wer das " +
          "nicht möchte, entfernt ihn über die Dokumentprüfung oder ändert ihn unter " +
          "„Datei“ → „Optionen“ → „Allgemein“ → „Benutzername“.",
      ],
      keyPoints: [
        "Datei → Informationen zeigt und bearbeitet die Dokumenteigenschaften.",
        "Der Titel wird in PDF, Suche und Dokumentbibliotheken angezeigt.",
        "Der Autorenname stammt aus der Office-Installation und wandert mit.",
      ],
      task: {
        instruction:
          "Trag im Feld „Titel“ der Dokumenteigenschaften einen aussagekräftigen Titel " +
          "für deine Präsentation ein.",
        hint: "Datei → Informationen, rechts in der Spalte auf das Feld „Titel“ klicken.",
        check: async () => {
          const eigenschaften = await readDocumentProperties();

          if (eigenschaften === null) {
            return nichtPruefbar(
              "Die Dokumenteigenschaften",
              "1.7",
              "Sieh unter „Datei“ → „Informationen“ nach, ob im Feld „Titel“ etwas steht.",
            );
          }

          const titel = eigenschaften.title;
          if (titel.length === 0) {
            return fail(
              "Das Feld „Titel“ der Dokumenteigenschaften ist leer.",
              "Datei → Informationen, dann rechts in das Feld „Titel“ klicken und einen Titel eintragen.",
            );
          }
          if (titel.split(/\s+/).length < 2) {
            return fail(
              `Als Titel steht bisher nur „${titel}“.`,
              "Ein Wort reicht in einer Dokumentbibliothek nicht zur Unterscheidung. Beschreibe kurz, worum es geht.",
            );
          }

          return pass(
            `Der Dokumenttitel lautet „${titel}“ – er erscheint in PDF, Suche und Dokumentbibliotheken.`,
          );
        },
      },
    },
    {
      id: "weitergabe",
      title: "Wie weitergegeben wird",
      explanation: [
        "Zum Schluss die Frage, wie die Datei zu den Empfängern kommt. Drei Wege sind " +
          "üblich, mit unterschiedlichen Folgen.",
        "Als Anhang per E-Mail: einfach, aber begrenzt auf etwa 20 MB, und jede Kopie " +
          "lebt ihr eigenes Leben. Sobald jemand etwas ändert, gibt es zwei Fassungen. " +
          "Für eine fertige Präsentation, die niemand mehr ändern soll, ist das " +
          "trotzdem der richtige Weg – am besten als PDF.",
        "Als Link auf OneDrive oder SharePoint: Alle sehen dieselbe Fassung, " +
          "Änderungen sind sofort für alle sichtbar, und die Zugriffsrechte lassen sich " +
          "steuern – etwa auf „Kann anzeigen“ statt „Kann bearbeiten“. Das ist der " +
          "richtige Weg für eine Präsentation, an der noch gearbeitet wird.",
        "Als schreibgeschützte Fassung: „Datei“ → „Informationen“ → „Präsentation " +
          "schützen“ → „Als abschließend kennzeichnen“ macht die Datei beim Öffnen " +
          "zunächst schreibgeschützt. Das ist ein Hinweis, keine Sicherung – ein Klick " +
          "auf „Trotzdem bearbeiten“ hebt ihn auf. Wer wirklich absichern will, vergibt " +
          "an derselben Stelle über „Mit Kennwort verschlüsseln“ ein Kennwort oder gibt " +
          "eine PDF weiter.",
      ],
      keyPoints: [
        "Datei → Informationen → Präsentation schützen → Als abschließend kennzeichnen.",
        "„Abschließend“ ist ein Hinweis, kein Schutz.",
        "Für gemeinsame Arbeit einen Link statt einer Kopie weitergeben.",
      ],
    },
  ],
};
